import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { WORKSHEETS, DOMAIN_ROWS, STAGES } from "@/config/worksheets";

const LIKERT = ["Never", "Rarely", "Sometimes", "Often", "Almost always"];
const BRAND = "#33475b";
const LINE = "#b9c2b3";
const GREY = "#d7d2c6";

const S = {
  page: { fontFamily: "Arial, Helvetica, sans-serif", color: "#111", background: "#fff", width: "210mm", minHeight: "297mm", margin: "0 auto 10mm", padding: "16mm 15mm", boxSizing: "border-box", position: "relative" },
  brand: { fontSize: 9.5, letterSpacing: "0.06em", color: BRAND, textTransform: "uppercase", fontWeight: 700 },
  rule: { border: 0, borderTop: `1px solid ${BRAND}`, margin: "4px 0 14px" },
  wsNum: { fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textAlign: "center", color: "#333" },
  wsTitle: { fontSize: 17, fontWeight: 700, textAlign: "center", margin: "2px 0 12px", lineHeight: 1.25 },
  meta: { display: "flex", flexWrap: "wrap", gap: "4px 24px", fontSize: 10.5, margin: "0 0 14px" },
  metaLine: { borderBottom: `1px solid ${GREY}`, display: "inline-block", minWidth: 90, padding: "0 4px" },
  instrLabel: { fontWeight: 700, fontSize: 10.5 },
  instr: { fontSize: 10.5, lineHeight: 1.5, margin: "0 0 12px" },
  legend: { display: "flex", flexWrap: "wrap", gap: "2px 16px", fontSize: 9.5, fontWeight: 600, color: "#444", margin: "0 0 6px" },
  th: { border: `1px solid ${GREY}`, background: "#eef2ec", fontSize: 9, fontWeight: 700, padding: "5px 6px", textAlign: "center" },
  thL: { border: `1px solid ${GREY}`, background: "#eef2ec", fontSize: 9, fontWeight: 700, padding: "5px 6px", textAlign: "left" },
  td: { border: `1px solid ${GREY}`, fontSize: 10, padding: "5px 8px", verticalAlign: "top" },
  cell: { border: `1px solid ${GREY}`, width: 30, textAlign: "center", fontSize: 10, padding: "5px 0" },
  cellOn: { border: `1px solid #1b4332`, width: 30, textAlign: "center", fontSize: 10, fontWeight: 700, padding: "5px 0", background: "#1b4332", color: "#fff" },
  sectionH: { fontSize: 11, fontWeight: 700, margin: "14px 0 6px" },
  chkGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 18px", fontSize: 9.8 },
  chkItem: { display: "flex", gap: 6, alignItems: "baseline" },
  otherBox: { border: `1px solid ${GREY}`, minHeight: 46, padding: "6px 8px", fontSize: 10, whiteSpace: "pre-wrap", marginTop: 4 },
  scoreWrap: { marginTop: 16 },
  scoreTbl: { width: "100%", borderCollapse: "collapse" },
  bands: { display: "flex", gap: 8, marginTop: 8 },
  footer: { position: "absolute", left: "15mm", right: "15mm", bottom: "8mm", borderTop: `1px solid ${GREY}`, paddingTop: 4, display: "flex", justifyContent: "space-between", fontSize: 8.5, color: "#666" },
};

function computePM(ws, data) {
  const lb = ws.blocks.find((b) => b.type === "likert");
  if (!lb) return null;
  let points = 0, denom = 0, answered = 0;
  lb.statements.forEach((_, i) => {
    const v = data["s" + i];
    if (v === "na") answered += 1;
    else if (typeof v === "number") { answered += 1; points += v; denom += 4; }
  });
  const complete = answered >= lb.statements.length && denom > 0;
  const score = complete ? Math.round((points / denom) * 100) / 10 : null;
  return { points, denom, score, answered, total: lb.statements.length };
}

const BANDS = [
  { key: "low", label: "Below 5: needs immediate attention", color: "#b91c1c", bg: "#fee2e2", test: (s) => s < 5 },
  { key: "mid", label: "5–7.9: needs improvement", color: "#b45309", bg: "#fef3c7", test: (s) => s >= 5 && s < 8 },
  { key: "high", label: "8–10: well done — maintain", color: "#15803d", bg: "#dcfce7", test: (s) => s >= 8 },
];

function Chk({ on }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        lineHeight: "12px",
        textAlign: "center",
        verticalAlign: "-2px",
        boxSizing: "border-box",
        border: "1.5px solid #111",
        borderRadius: 2,
        background: on ? "#111" : "#fff",
        color: "#fff",
        fontSize: 10,
        fontWeight: 700,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {on ? "\u2713" : ""}
    </span>
  );
}

function Blocks({ ws, data, dash }) {
  const d = data || {};
  return ws.blocks.map((b, bi) => {
    if (b.type === "instructions")
      return <p key={bi} style={S.instr}><span style={S.instrLabel}>Instructions: </span>{b.text}</p>;

    if (b.type === "likert")
      return (
        <div key={bi}>
          <div style={S.legend}>{LIKERT.map((l, i) => <span key={i}>{i} = {l}</span>)}</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.thL}>Health statement</th>
                {[0, 1, 2, 3, 4].map((n) => <th key={n} style={S.th}>{n}</th>)}
              </tr>
            </thead>
            <tbody>
              {b.statements.map((st, i) => {
                const v = d["s" + i];
                const na = v === "na";
                return (
                  <tr key={`${st}-${i}`}>
                    <td style={S.td}>{i + 1}. {st}{na ? "  (N/A)" : ""}</td>
                    {[0, 1, 2, 3, 4].map((n) => (
                      <td key={n} style={v === n ? S.cellOn : S.cell}>{v === n ? n : ""}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );

    if (b.type === "checklist")
      return (
        <div key={bi}>
          <div style={S.sectionH}>{b.title}</div>
          {b.note && <div style={{ fontSize: 9, color: "#666", margin: "0 0 6px" }}>{b.note}</div>}
          <div style={S.chkGrid}>
            {b.items.map((it, i) => (
              <div key={`${it}-${i}`} style={S.chkItem}>
                <Chk on={!!d["chk_" + i]} />
                <span>{it}{d["note_" + i] ? <em style={{ color: "#1b4332" }}> — {d["note_" + i]}</em> : ""}</span>
              </div>
            ))}
          </div>
        </div>
      );

    if (b.type === "textarea")
      return (
        <div key={bi} style={{ marginTop: 12 }}>
          <div style={S.instrLabel}>{b.label}</div>
          <div style={S.otherBox}>{d[b.key] || "\u00A0"}</div>
        </div>
      );

    if (b.type === "field")
      return <div key={bi} style={{ marginTop: 8, fontSize: 10.5 }}><b>{b.label}: </b><span style={S.metaLine}>{d[b.key] || "\u00A0"}</span></div>;

    if (b.type === "fields")
      return (
        <div key={bi} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px", fontSize: 10.5, marginTop: 8 }}>
          {b.items.map((f) => <div key={f.key}><b>{f.label}: </b><span style={S.metaLine}>{d[f.key] || "\u00A0"}</span></div>)}
        </div>
      );

    if (b.type === "checkboxes")
      return (
        <div key={bi} style={{ marginTop: 12 }}>
          <div style={S.sectionH}>{b.title}</div>
          <div style={{ display: "flex", gap: 18, fontSize: 10.5 }}>
            {b.items.map((it) => <span key={it.key} style={S.chkItem}><Chk on={!!d[it.key]} /> {it.label}</span>)}
          </div>
        </div>
      );

    if (b.type === "rowtable") {
      const rows = b.rowLabels || Array.from({ length: b.rows }, (_, i) => null);
      return (
        <div key={bi} style={{ marginTop: 12 }}>
          {b.title && <div style={S.sectionH}>{b.title}</div>}
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {b.rowLabels && <th style={S.thL}></th>}
                {b.columns.map((c) => <th key={c.key} style={S.thL}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((rl, r) => (
                <tr key={r}>
                  {b.rowLabels && <td style={{ ...S.td, fontWeight: 700 }}>{rl}</td>}
                  {b.columns.map((c) => <td key={c.key} style={S.td}>{d[`${b.key}_${r}_${c.key}`] || "\u00A0"}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (b.type === "dashboard")
      return (
        <table key={bi} style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
          <thead>
            <tr>
              <th style={S.thL}>Health domain</th>
              {STAGES.map((s) => <th key={s} style={S.th}>{s}</th>)}
            </tr>
          </thead>
          <tbody>
            {DOMAIN_ROWS.map((row) => (
              <tr key={row.wid}>
                <td style={S.td}>{row.label}</td>
                {STAGES.map((s) => <td key={s} style={{ ...S.td, textAlign: "center" }}>{dash?.by_stage?.[s]?.[row.wid] ?? "\u00A0"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );

    return null;
  });
}

function WorksheetPage({ ws, data, dash, page, participant, stage }) {
  const pm = ws.blocks.some((b) => b.type === "likert") ? computePM(ws, data) : null;
  return (
    <div className="print-page" style={S.page}>
      <div style={S.brand}>Ayurtoday Ayurveda &nbsp;|&nbsp; Corporate Wellness Programme</div>
      <hr style={S.rule} />
      <div style={S.wsNum}>WORKSHEET {ws.num}</div>
      <div style={S.wsTitle}>{ws.title}</div>
      <div style={S.meta}>
        <span>Participant code: <span style={S.metaLine}>{participant || "\u00A0"}</span></span>
        <span>Date: <span style={S.metaLine}>{new Date().toLocaleDateString()}</span></span>
        <span>Assessment stage: <b>{stage}</b> &nbsp;<span style={{ color: "#999" }}>Baseline / Month 1 / Month 2 / Month 3 / Other</span></span>
      </div>

      <Blocks ws={ws} data={data} dash={dash} />

      {pm && (
        <div style={S.scoreWrap}>
          <table style={S.scoreTbl}>
            <thead>
              <tr>
                <th style={S.thL}>Scoring</th>
                <th style={S.th}>Points obtained</th>
                <th style={S.th}>Maximum (8 × 4)</th>
                <th style={S.th}>Domain score /10</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...S.td, fontSize: 9, color: "#555" }}>Formula: points obtained ÷ maximum × 10</td>
                <td style={{ ...S.td, textAlign: "center" }}>{pm.points}</td>
                <td style={{ ...S.td, textAlign: "center" }}>{pm.denom || 32}</td>
                <td style={{ ...S.td, textAlign: "center", fontWeight: 700, fontSize: 12 }}>{pm.score ?? "\u00A0"}</td>
              </tr>
            </tbody>
          </table>
          <div style={S.bands}>
            {BANDS.map((band) => {
              const active = pm.score != null && band.test(pm.score);
              return (
                <div key={band.key} style={{ flex: 1, textAlign: "center", padding: "5px 6px", fontSize: 9.5, fontWeight: 700, borderRadius: 3, border: `1px solid ${active ? band.color : GREY}`, background: active ? band.bg : "#fafafa", color: active ? band.color : "#9ca3af" }}>
                  {band.label}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 8.5, color: "#777", marginTop: 4 }}>A serious symptom can require medical attention even when the overall score is high.</div>
        </div>
      )}

      <div style={S.footer}>
        <span>Participant Assessment Worksheet Booklet &nbsp;|&nbsp; Confidential participant information</span>
        <span>Page {page}</span>
      </div>
    </div>
  );
}

export default function PrintView() {
  const { id } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [responses, setResponses] = useState({});
  const [dash, setDash] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/assessments/${id}`),
      api.get(`/assessments/${id}/dashboard`),
    ]).then(([a, dd]) => {
      setAssessment(a.data); setResponses(a.data.responses || {}); setDash(dd.data);
      const prevTitle = document.title;
      const name = (a.data.participant_name || "Participant").trim().replace(/[\\/:*?"<>|]+/g, "-");
      document.title = `Ayur Today - ${name} - Assessment`;
      const restore = () => { document.title = prevTitle; };
      window.addEventListener("afterprint", restore, { once: true });
      setTimeout(() => window.print(), 900);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!assessment) return <div style={{ padding: 40, fontFamily: "Arial", color: "#666" }}>Preparing document…</div>;

  return (
    <div style={{ background: "#e9e9e9", padding: "10mm 0" }}>
      {WORKSHEETS.map((ws, i) => (
        <WorksheetPage key={ws.id} ws={ws} data={responses[ws.id]?.data || {}} dash={dash}
          page={i + 1} participant={assessment.participant_name} stage={assessment.stage} />
      ))}
    </div>
  );
}
